import React, { useState, useEffect, useRef, useMemo } from "react";
import { FaArrowLeft, FaChevronDown } from "react-icons/fa";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "../assets/css/EditTenant.css";
import "../assets/css/Projects.css";
import "../assets/css/DigixLeads.css";
import "../assets/css/Issues.css";
import axiosInstance from "../components/api/axiosInstance";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import { currencyRowsFromResponseData, normalizeCurrencyRow } from "../utils/currencyList";
import { getAuthItem } from "../utils/authStorage";
import {
  getCustomerSelectLabel,
  getCustomerSelectSearchText,
  mapCustomerListItemFromApi
} from "../utils/formatters";

const VISIBILITY_OPTIONS = ["Private", "Public", "Team"];

function EditProject({ onBack, onUpdateProject }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const navigateBackToProjects = () => {
    navigate("/projects", { state: { projectManagementTab: "Projects" } });
  };
  const role = useMemo(() => {
    try {
      const u = getAuthItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);
  const [formData, setFormData] = useState({
    projectName: "",
    shortCode: "",
    description: "",
    notes: "",
    domain: "",
    category: "",
    startDate: "",
    endDate: "",
    actualStartDate: "",
    actualEndDate: "",
    totalCost: "",
    currencyId: "",
    prioritySeq: "",
    visibility: "Private",
    status: "",
    statusId: "",
    clientId: "",
    userIds: []
  });
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [teamMemberSearchQuery, setTeamMemberSearchQuery] = useState("");
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [domainSearchQuery, setDomainSearchQuery] = useState("");
  const domainDropdownRef = useRef(null);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const customerDropdownRef = useRef(null);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const categoryDropdownRef = useRef(null);
  const teamDropdownRef = useRef(null);
  const [statuses, setStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(true);
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);
  const [currencyFetchError, setCurrencyFetchError] = useState(null);

  // Project subscription (FICO) - same as Add Project
  const [subscriptionForm, setSubscriptionForm] = useState({
    createOnProjectUpdate: false,
    planId: "1",
    startDate: "",
    endDate: "",
    status: "Active",
    amount: ""
  });
  const [subscriptionResult, setSubscriptionResult] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState("");
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [editingSubscriptionId, setEditingSubscriptionId] = useState(null);
  const [loadingEditingSubscription, setLoadingEditingSubscription] = useState(false);

  const normalizeDateInput = (v) => {
    if (v == null || v === "") return "";
    const s = String(v);
    return s.length >= 10 ? s.slice(0, 10) : s;
  };

  useEffect(() => {
    const loadUsersMerged = async (projectUserRows) => {
      setLoadingUsers(true);
      try {
        const response = await axiosInstance.get("/users");
        const arr = Array.isArray(response.data)
          ? response.data
          : response.data?.data || response.data?.users || [];
        const list = arr
          .map((u) => {
            const uid = Number(u.user_id ?? u.User_Id ?? u.id);
            const name =
              `${u.first_name || u.firstName || ""} ${u.last_name || u.lastName || ""}`.trim() ||
              u.email ||
              "—";
            return { id: uid, name };
          })
          .filter((x) => !Number.isNaN(x.id));
        const byId = new Map(list.map((u) => [u.id, u]));
        for (const u of projectUserRows || []) {
          const uid = Number(u.User_Id ?? u.user_id);
          if (!Number.isNaN(uid) && !byId.has(uid)) {
            byId.set(uid, { id: uid, name: u.User_Name ?? u.user_name ?? "—" });
          }
        }
        setUsers([...byId.values()].sort((a, b) => a.id - b.id));
      } catch (err) {
        console.error("Error fetching users:", err);
        const fallback = (projectUserRows || []).map((u) => ({
          id: Number(u.User_Id ?? u.user_id),
          name: u.User_Name ?? u.user_name ?? "—"
        })).filter((x) => !Number.isNaN(x.id));
        setUsers(fallback);
      } finally {
        setLoadingUsers(false);
      }
    };

    const fetchProject = async () => {
      if (!id) return;
      setLoading(true);
      try {
        let p = null;
        try {
          const response = await axiosInstance.get(`/project/${id}`);
          p = response.data?.data ?? response.data;
        } catch {
          const response = await axiosInstance.get(`/projects/${id}`);
          p = response.data?.data ?? response.data;
        }
        if (p && typeof p === "object") {
          const userIds = (p.Users || [])
            .map((u) => Number(u.User_Id ?? u.user_id))
            .filter((n) => !Number.isNaN(n));
          const totalRaw = p.Total_Cost ?? p.total_cost;
          setFormData({
            projectName: p.Project_Name || p.project_name || "",
            shortCode: p.Short_Code || p.short_code || "",
            description: p.Description || p.description || "",
            notes: p.Notes || p.notes || "",
            domain: (p.Domain_Id ?? p.domain_id ?? "") !== "" ? String(p.Domain_Id ?? p.domain_id) : "",
            category: (p.Category_Id ?? p.category_id ?? "") !== "" ? String(p.Category_Id ?? p.category_id) : "",
            startDate: normalizeDateInput(p.Start_Date || p.start_date),
            endDate: normalizeDateInput(p.End_Date || p.end_date),
            actualStartDate: normalizeDateInput(p.Actual_Start_Date || p.actual_start_date),
            actualEndDate: normalizeDateInput(p.Actual_End_Date || p.actual_end_date),
            prioritySeq: p.Priority_Seq || p.priority_seq || p.PrioritySeq || p.prioritySeq || "",
            totalCost: totalRaw != null && totalRaw !== "" ? String(totalRaw) : "",
            currencyId:
              (p.Currency_Id ?? p.currency_id) != null && String(p.Currency_Id ?? p.currency_id).trim() !== ""
                ? String(p.Currency_Id ?? p.currency_id)
                : "",
            visibility: p.Visibility || p.visibility || "Private",
            status: p.Status || p.status || "",
            statusId:
              (p.Status_Id ?? p.status_id ?? p.StatusId ?? p.statusId) != null &&
              String(p.Status_Id ?? p.status_id ?? p.StatusId ?? p.statusId).trim() !== ""
                ? String(p.Status_Id ?? p.status_id ?? p.StatusId ?? p.statusId)
                : "",
            clientId: String(p.client_id ?? p.client_id ?? ""),
            userIds
          });
          await loadUsersMerged(p.Users || []);
        }
      } catch (err) {
        console.error("Error fetching project:", err);
        alert(err.response?.data?.message || err.response?.data?.error || "Failed to load project.");
      } finally {
        setLoading(false);
      }
    };
    const fetchCustomers = async () => {
      try {
        const response = await axiosInstance.get("/clients");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setCustomers(arr.map(mapCustomerListItemFromApi).filter(Boolean));
      } catch (err) {
        console.error("Error fetching customers:", err);
      }
    };
    const fetchCurrencies = async () => {
      setLoadingCurrencies(true);
      setCurrencyFetchError(null);
      const fallbackList = [
        { id: 1, name: "Indian Rupee", code: "INR", symbol: "₹" },
        { id: 2, name: "US Dollar", code: "USD", symbol: "$" }
      ];
      try {
        const response = await axiosInstance.get("/currencies");
        const rows = currencyRowsFromResponseData(response.data);
        const mapped = rows.map(normalizeCurrencyRow).filter(Boolean);
        const filtered = mapped.filter((c) => {
          const code = String(c?.code ?? "").trim().toUpperCase();
          const symbol = String(c?.symbol ?? "").trim();
          const isInr = code === "INR" || symbol === "₹";
          // Prefer code USD; only allow symbol "$" if code is missing.
          const isUsd = code === "USD" || (code === "" && symbol === "$");
          return isInr || isUsd;
        });

        setCurrencies(filtered.length > 0 ? filtered : fallbackList);
        if (mapped.length === 0 && rows.length > 0) {
          setCurrencyFetchError("Currencies were returned in an unexpected format; showing INR and USD as fallback.");
        } else if (mapped.length > 0 && filtered.length === 0) {
          setCurrencyFetchError("Only INR (₹) and USD ($) are supported; showing INR and USD as fallback.");
        }
      } catch (err) {
        console.error("Error fetching currencies:", err);
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Could not load currencies.";
        setCurrencyFetchError(String(msg));
        setCurrencies(fallbackList);
      } finally {
        setLoadingCurrencies(false);
      }
    };
    const fetchDomains = async () => {
      try {
        const response = await axiosInstance.get("/domains");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setDomains(
          arr
            .map((d) => {
              const id = d.Domain_Id || d.domain_id || d.id;
              const name = d.Domain_Name || d.domain_name || d.name || "—";
              return { id, name };
            })
            .filter((x) => x.id)
        );
      } catch (err) {
        console.error("Error fetching domains:", err);
        setDomains([]);
      } finally {
        setLoadingDomains(false);
      }
    };
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get("/project-categories");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setCategories(
          arr
            .map((c) => {
              const id = c.Category_Id || c.category_id || c.id;
              const name = c.Category_Name || c.category_name || c.name || "—";
              return { id, name };
            })
            .filter((x) => x.id)
        );
      } catch (err) {
        console.error("Error fetching project categories:", err);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    const fetchStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const response = await axiosInstance.get("/project-statuses");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        const mapped = arr
          .map((s) => ({
            id: s.Status_Id ?? s.status_id ?? s.id,
            name: s.Status ?? s.status ?? s.name ?? "—"
          }))
          .filter((x) => x.id)
          .sort((a, b) => a.id - b.id);
        setStatuses(mapped);
      } catch (err) {
        console.error("Error fetching project statuses:", err);
      } finally {
        setLoadingStatuses(false);
      }
    };
    fetchProject();
    fetchCustomers();
    fetchDomains();
    fetchCategories();
    fetchStatuses();
    fetchCurrencies();
  }, [id]);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoadingPlans(true);
      const fallback = [{ id: 1, name: "Monthly" }];
      try {
        const res = await axiosInstance.get("/fico/subscription-plans");
        const arr = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
        const mapped = (arr || [])
          .map((p) => {
            const pid = Number(p?.Plan_Id ?? p?.plan_id ?? p?.id);
            const name = String(p?.Plan_Name ?? p?.plan_name ?? p?.name ?? "").trim();
            if (!pid || Number.isNaN(pid)) return null;
            return { id: pid, name: name || `Plan ${pid}` };
          })
          .filter(Boolean);
        const finalPlans = mapped.length > 0 ? mapped : fallback;
        setPlans(finalPlans);
        setSubscriptionForm((prev) => {
          const stillValid = finalPlans.some((pl) => String(pl.id) === String(prev.planId));
          if (stillValid) return prev;
          return { ...prev, planId: String(finalPlans[0]?.id ?? 1) };
        });
      } catch (err) {
        console.error("Error fetching subscription plans:", err);
        setPlans(fallback);
        setSubscriptionForm((prev) => (prev.planId ? prev : { ...prev, planId: "1" }));
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    // If navigated from "Edit subscription" action, we open Edit Project with:
    // /projects/:id/edit?subscriptionId=2
    const sp = new URLSearchParams(location.search || "");
    const subIdRaw = sp.get("subscriptionId");
    const subIdNum = subIdRaw != null ? Number(subIdRaw) : null;
    if (!subIdNum || Number.isNaN(subIdNum)) {
      setEditingSubscriptionId(null);
      return;
    }
    setEditingSubscriptionId(subIdNum);

    const loadSubscription = async () => {
      setLoadingEditingSubscription(true);
      setSubscriptionError("");
      setSubscriptionResult(null);
      try {
        const res = await axiosInstance.get(`/fico/project-subscription/${subIdNum}`);
        const s = res?.data?.data ?? res?.data ?? null;
        if (s) {
          setSubscriptionForm((prev) => ({
            ...prev,
            createOnProjectUpdate: true,
            planId: String(s.Plan_Id ?? s.plan_id ?? prev.planId ?? "1"),
            startDate: normalizeDateInput(s.Start_Date ?? s.start_date),
            endDate: normalizeDateInput(s.End_Date ?? s.end_date),
            status: String(s.Status ?? s.status ?? prev.status ?? "Active"),
            amount: s.Amount ?? s.amount ?? ""
          }));
        }
      } catch (err) {
        console.error("Error fetching subscription for edit:", err);
        setSubscriptionError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            err.message ||
            "Failed to load subscription for editing."
        );
      } finally {
        setLoadingEditingSubscription(false);
      }
    };

    loadSubscription();
  }, [location.search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(e.target)) { setDomainDropdownOpen(false); setDomainSearchQuery(""); }
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target)) { setCustomerDropdownOpen(false); setCustomerSearchQuery(""); }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) { setCategoryDropdownOpen(false); setCategorySearchQuery(""); }
      if (teamDropdownRef.current && !teamDropdownRef.current.contains(e.target)) { setShowTeamDropdown(false); setTeamMemberSearchQuery(""); }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubscriptionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSubscriptionForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const today = new Date().toISOString().split("T")[0];

  const handleUserToggle = (userId) => {
    const uid = Number(userId);
    setFormData((prev) => {
      const ids = prev.userIds.map(Number);
      const next = ids.includes(uid) ? ids.filter((x) => x !== uid) : [...ids, uid];
      return { ...prev, userIds: next };
    });
  };

  const selectedUserIdSet = useMemo(() => new Set(formData.userIds.map(Number)), [formData.userIds]);

  const allUsersSelected =
    !loadingUsers && users.length > 0 && users.every((u) => selectedUserIdSet.has(Number(u.id)));

  const handleToggleSelectAllUsers = () => {
    setFormData((prev) => {
      if (allUsersSelected) {
        return { ...prev, userIds: [] };
      }
      return { ...prev, userIds: users.map((u) => Number(u.id)) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.projectName || !formData.shortCode || !formData.clientId) {
      alert("Please fill in required fields (Project Name, Short Code, Tenant)");
      return;
    }
    if (!formData.currencyId) {
      alert("Please select a currency.");
      return;
    }
    const totalCostNum = Number(String(formData.totalCost || "0").replace(/,/g, ""));
    if (Number.isNaN(totalCostNum) || totalCostNum < 0) {
      alert("Please enter a valid total cost (0 or greater).");
      return;
    }
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      alert("End date cannot be before start date.");
      return;
    }
    if (formData.actualStartDate && formData.actualEndDate && formData.actualEndDate < formData.actualStartDate) {
      alert("Actual end date cannot be before actual start date.");
      return;
    }
    let updatedBy = 1;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        updatedBy = user.user_id || user.id || user.User_Id || 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }
    const payload = {
      client_id: Number(formData.clientId),
      Project_Name: formData.projectName.trim(),
      Short_Code: formData.shortCode.trim(),
      Description: formData.description?.trim() || null,
      Total_Cost: totalCostNum,
      Currency_Id: Number(formData.currencyId),
      Domain_Id: formData.domain ? Number(formData.domain) : null,
      Category_Id: formData.category ? Number(formData.category) : null,
      Start_Date: formData.startDate || null,
      End_Date: formData.endDate || null,
      Actual_Start_Date: formData.actualStartDate || null,
      Actual_End_Date: formData.actualEndDate || null,
      Priority_Seq: formData.prioritySeq?.trim() || null,
      Status_Id: formData.statusId ? parseInt(formData.statusId, 10) : null,
      Visibility: formData.visibility || "Private",
      Notes: formData.notes?.trim() || null,
      Updated_By: updatedBy,
      User_Ids: (formData.userIds || []).map((uid) => Number(uid))
    };
    setSubmitting(true);
    try {
      const response = await axiosInstance.put(`/project/${id}`, payload);
      const body = response.data?.data ?? response.data;
      if (response.status === 200 || response.status === 201 || body) {
        // Optional: create a subscription after update (best-effort)
        setSubscriptionResult(null);
        setSubscriptionError("");
        let subscriptionResp = null;
        let subscriptionErrMsg = "";
        if (subscriptionForm.createOnProjectUpdate) {
          const start = subscriptionForm.startDate;
          let end = subscriptionForm.endDate;
          const planIdNum = Number(subscriptionForm.planId || "1");
          const status = subscriptionForm.status || "Active";

          if (start && !end) {
            try {
              const dt = new Date(start + "T00:00:00");
              dt.setDate(dt.getDate() + 30);
              end = dt.toISOString().split("T")[0];
            } catch {
              // ignore
            }
          }

          if (!start || !end) {
            subscriptionErrMsg = "Subscription dates are missing (Start/End).";
          } else if (!planIdNum || Number.isNaN(planIdNum) || planIdNum <= 0) {
            subscriptionErrMsg = "Invalid plan for subscription.";
          } else if (end < start) {
            subscriptionErrMsg = "Subscription End Date cannot be before Start Date.";
          } else {
            try {
              const amountStr = String(subscriptionForm.amount ?? "").trim();
              const amountNum =
                amountStr === "" ? null : Number(String(amountStr).replace(/,/g, ""));
              if (amountNum != null && (Number.isNaN(amountNum) || amountNum < 0)) {
                throw new Error("Invalid amount (0 or greater).");
              }

              if (editingSubscriptionId) {
                // Update existing subscription
                const updatePayload = {
                  Project_Id: Number(id),
                  Plan_Id: planIdNum,
                  Amount: amountStr === "" ? null : amountStr,
                  Start_Date: start,
                  End_Date: end,
                  Status: status,
                  Updated_By: updatedBy
                };
                let subRes = null;
                try {
                  subRes = await axiosInstance.put(`/fico/update-project-subscription/${editingSubscriptionId}`, updatePayload);
                } catch {
                  subRes = await axiosInstance.post(`/fico/update-project-subscription/${editingSubscriptionId}`, updatePayload);
                }
                const subRaw = subRes?.data;
                subscriptionResp = subRaw?.data ?? subRaw ?? null;
              } else {
                // Create new subscription
                const subPayload = {
                  Project_Id: Number(id),
                  Plan_Id: planIdNum,
                  Start_Date: start,
                  End_Date: end,
                  Status: status,
                  Created_By: updatedBy
                };
                if (amountNum != null && !Number.isNaN(amountNum)) {
                  subPayload.Amount = amountNum;
                }
                const subRes = await axiosInstance.post("/fico/project-subscription", subPayload);
                const subRaw = subRes?.data;
                subscriptionResp = subRaw?.data ?? subRaw ?? null;
              }
            } catch (subErr) {
              subscriptionErrMsg =
                subErr.response?.data?.message ||
                subErr.response?.data?.error ||
                subErr.message ||
                (editingSubscriptionId ? "Failed to update project subscription." : "Failed to create project subscription.");
            }
          }
        }

        if (subscriptionResp) setSubscriptionResult(subscriptionResp);
        if (subscriptionErrMsg) setSubscriptionError(subscriptionErrMsg);

        if (subscriptionResp) {
          alert(`Project updated successfully and subscription ${editingSubscriptionId ? "updated" : "created"}.`);
        } else if (subscriptionErrMsg) {
          alert((response.data?.message || "Project updated successfully") + ", but subscription failed: " + subscriptionErrMsg);
        } else {
          alert(response.data?.message || "Project updated successfully");
        }
        if (onUpdateProject) onUpdateProject();
        if (onBack) onBack();
        else navigateBackToProjects();
      } else {
        throw new Error("Update failed - no response data");
      }
    } catch (err) {
      console.error("Error updating project:", err);
      alert(err.response?.data?.message || err.response?.data?.error || "Failed to update project.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="project-form-container edittenant-container">
        <div className="edittenant-loading">Loading project...</div>
      </div>
    );
  }

  return (
    <div className="project-form-container edittenant-container">
      <div className="viewlead-header">
        <button
          type="button"
          className="viewlead-back-link"
          onClick={navigateBackToProjects}
        >
          <FaArrowLeft /> Back
        </button>
      </div>
      <div className="issues-main-card">
      <form className="edittenant-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label>Project Name <span className="required">*</span></label>
              <input type="text" name="projectName" value={formData.projectName} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>Short Code <span className="required">*</span></label>
              <input type="text" name="shortCode" value={formData.shortCode} onChange={handleInputChange} required  maxLength={20} />
            </div>
            <div className="form-group">
              <label>Tenant <span className="required">*</span></label>
              <div className="domain-filter-dropdown" ref={customerDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", cursor: "pointer" }} onClick={() => setCustomerDropdownOpen((o) => !o)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setCustomerDropdownOpen((o) => !o); } }}>
                  <span>{formData.clientId ? (() => { const sel = customers.find((c) => String(c.id) === String(formData.clientId)); return sel ? getCustomerSelectLabel(sel) : "Select Tenant"; })() : "Select Tenant"}</span>
                  <FaChevronDown className={`domain-filter-chevron ${customerDropdownOpen ? "open" : ""}`} />
                </div>
                {customerDropdownOpen && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search"  value={customerSearchQuery} onChange={(e) => setCustomerSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.clientId ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, clientId: "" })); setCustomerDropdownOpen(false); setCustomerSearchQuery(""); }}>Select Tenant</div>
                      {customers.filter((c) => !customerSearchQuery.trim() || getCustomerSelectSearchText(c).includes(customerSearchQuery.trim().toLowerCase())).map((c) => (
                        <div key={c.id} className={`domain-filter-option ${String(formData.clientId) === String(c.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, clientId: String(c.id) })); setCustomerDropdownOpen(false); setCustomerSearchQuery(""); }}>{getCustomerSelectLabel(c)}</div>
                      ))}
                      {customers.filter((c) => !customerSearchQuery.trim() || getCustomerSelectSearchText(c).includes(customerSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No tenants found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Domain</label>
              <div className="domain-filter-dropdown" ref={domainDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingDomains ? 0.6 : 1, cursor: loadingDomains ? "default" : "pointer" }} onClick={() => !loadingDomains && setDomainDropdownOpen((o) => !o)} role="button" tabIndex={loadingDomains ? -1 : 0} onKeyDown={(e) => { if (!loadingDomains && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setDomainDropdownOpen((o) => !o); } }}>
                  <span>{loadingDomains ? "Loading domains..." : (formData.domain ? (domains.find((d) => String(d.id) === String(formData.domain))?.name || "Select Domain") : "Select Domain")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${domainDropdownOpen ? "open" : ""}`} />
                </div>
                {domainDropdownOpen && !loadingDomains && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search domains..." value={domainSearchQuery} onChange={(e) => setDomainSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.domain ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: "" })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>Select Domain</div>
                      {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).map((d) => (
                        <div key={d.id} className={`domain-filter-option ${String(formData.domain) === String(d.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: String(d.id) })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>{d.name}</div>
                      ))}
                      {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No domains found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Category</label>
              <div className="domain-filter-dropdown" ref={categoryDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingCategories ? 0.6 : 1, cursor: loadingCategories ? "default" : "pointer" }} onClick={() => !loadingCategories && setCategoryDropdownOpen((o) => !o)} role="button" tabIndex={loadingCategories ? -1 : 0} onKeyDown={(e) => { if (!loadingCategories && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setCategoryDropdownOpen((o) => !o); } }}>
                  <span>{loadingCategories ? "Loading categories..." : (formData.category ? (categories.find((c) => String(c.id) === String(formData.category))?.name || "Select Category") : "Select Category")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${categoryDropdownOpen ? "open" : ""}`} />
                </div>
                {categoryDropdownOpen && !loadingCategories && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search categories..." value={categorySearchQuery} onChange={(e) => setCategorySearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.category ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, category: "" })); setCategoryDropdownOpen(false); setCategorySearchQuery(""); }}>Select Category</div>
                      {categories.filter((c) => !categorySearchQuery.trim() || String(c.name || "").toLowerCase().includes(categorySearchQuery.trim().toLowerCase())).map((c) => (
                        <div key={c.id} className={`domain-filter-option ${String(formData.category) === String(c.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, category: String(c.id) })); setCategoryDropdownOpen(false); setCategorySearchQuery(""); }}>{c.name}</div>
                      ))}
                      {categories.filter((c) => !categorySearchQuery.trim() || String(c.name || "").toLowerCase().includes(categorySearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No categories found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Visibility</label>
              <select name="visibility" value={formData.visibility} onChange={handleInputChange}>
                {VISIBILITY_OPTIONS.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="statusId"
                value={formData.statusId}
                onChange={handleInputChange}
                disabled={loadingStatuses}
              >
                <option value="">{loadingStatuses ? "Loading statuses..." : "Select Status"}</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Total cost <span className="required">*</span>
              </label>
              <input
                type="number"
                name="totalCost"
                min="0"
                step="0.01"
                value={formData.totalCost}
                onChange={handleInputChange}
              
                required
              />
            </div>
            <div className="form-group">
              <label>
                Currency <span className="required">*</span>
              </label>
              <select
                name="currencyId"
                value={formData.currencyId}
                onChange={handleInputChange}
                disabled={loadingCurrencies}
                required
              >
                <option value="">{loadingCurrencies ? "Loading currencies..." : "Select currency"}</option>
                {currencies.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {(c.symbol || c.code || c.name || "").trim()}
                  </option>
                ))}
              </select>
              {currencyFetchError && !loadingCurrencies ? (
                <p style={{ margin: "6px 0 0", fontSize: 12, color: "#b42318" }}>{currencyFetchError}</p>
              ) : null}
            </div>
            <div className="form-group">
              <label>Start Date</label>
              <input type="date" name="startDate" value={formData.startDate} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                min={formData.startDate || undefined}
              />
            </div>
            <div className="form-group">
              <label>Actual start date</label>
              <input
                type="date"
                name="actualStartDate"
                value={formData.actualStartDate}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Actual end date</label>
              <input
                type="date"
                name="actualEndDate"
                value={formData.actualEndDate}
                onChange={handleInputChange}
                min={formData.actualStartDate || undefined}
              />
            </div>
            <div className="form-group project-priority-seq-group">
              <label>Priority Sequence</label>
              <input
                type="text"
                name="prioritySeq"
                value={formData.prioritySeq}
                onChange={handleInputChange}
              
              />
            </div>
            <div className="form-group form-group-multi-select project-team-members-inline-group">
              <label>Team Members (Users)</label>
              <div ref={teamDropdownRef} style={{width: "100%", maxWidth: "100%" }}>
                <div
                  className="permissions-select-trigger"
                  onClick={() => {
                    if (!loadingUsers) setShowTeamDropdown((prev) => !prev);
                  }}
                  style={{
                    opacity: loadingUsers ? 0.6 : 1,
                    cursor: loadingUsers ? "default" : "pointer"
                  }}
                >
                  <span className="permissions-select-text">
                    {loadingUsers
                      ? "Loading team members..."
                      : formData.userIds.length === 0
                        ? "Select team members"
                        : `${formData.userIds.length} member${formData.userIds.length > 1 ? "s" : ""} selected`}
                  </span>
                  <FaChevronDown
                    className={`permissions-select-chevron ${showTeamDropdown ? "open" : ""}`}
                  />
                </div>
                {showTeamDropdown && !loadingUsers && (
                  <div
                    className="permissions-select-panel"
                    style={{ maxHeight: 220, overflowY: "auto" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="text"
                      className="domain-filter-search"
                      placeholder="Search team members..."
                      value={teamMemberSearchQuery}
                      onChange={(e) => setTeamMemberSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ margin: "8px", width: "calc(100% - 16px)", boxSizing: "border-box" }}
                    />
                    <label className="permissions-select-item">
                      <input
                        type="checkbox"
                        checked={allUsersSelected}
                        onChange={handleToggleSelectAllUsers}
                      />
                      <span className="permissions-select-item-label">Select all</span>
                    </label>
                    {users
                      .filter(
                        (u) =>
                          !teamMemberSearchQuery.trim() ||
                          String(u.name || "").toLowerCase().includes(teamMemberSearchQuery.trim().toLowerCase())
                      )
                      .map((u) => (
                        <label key={u.id} className="permissions-select-item">
                          <input
                            type="checkbox"
                            checked={selectedUserIdSet.has(Number(u.id))}
                            onChange={() => handleUserToggle(u.id)}
                          />
                          <span className="permissions-select-item-label">{u.name}</span>
                        </label>
                      ))}
                    {users.filter(
                      (u) =>
                        !teamMemberSearchQuery.trim() ||
                        String(u.name || "").toLowerCase().includes(teamMemberSearchQuery.trim().toLowerCase())
                    ).length === 0 && (
                      <div className="domain-filter-no-results" style={{ padding: "12px" }}>
                        No team members found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="form-group" style={{ marginTop: 16 }}>
            <label>Description</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} rows="4" />
          </div>
          <div className="form-group" style={{ marginTop: 16 }}>
            <label>Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="4"  />
          </div>

          <div style={{ gridColumn: "1 / -1", marginTop: 16 }}>
            <h4 style={{ margin: "0 0 10px 0", fontSize: 16, fontWeight: 600, color: "#172B4D" }}>
              Project Subscription
            </h4>

            <label className="checkbox-label" style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                name="createOnProjectUpdate"
                checked={subscriptionForm.createOnProjectUpdate}
                onChange={handleSubscriptionChange}
                disabled={!!editingSubscriptionId}
              />
              <span>{editingSubscriptionId ? "Update subscription with this project update" : "Create subscription on project update"}</span>
            </label>

            {loadingEditingSubscription ? (
              <div className="muted-text" style={{ marginTop: 10 }}>Loading subscription...</div>
            ) : null}

            <div className="project-subscription-form-grid" style={{ marginTop: 12 }}>
              <div className="form-group">
                <label>Plan Name</label>
                <select
                  name="planId"
                  value={subscriptionForm.planId}
                  onChange={handleSubscriptionChange}
                  disabled={!subscriptionForm.createOnProjectUpdate || loadingPlans}
                >
                  <option value="">{loadingPlans ? "Loading plans..." : "Select plan"}</option>
                  {plans.map((pl) => (
                    <option key={pl.id} value={String(pl.id)}>
                      {pl.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Subscription Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={subscriptionForm.startDate}
                  onChange={handleSubscriptionChange}
                  min={today}
                  disabled={!subscriptionForm.createOnProjectUpdate}
                />
              </div>

              <div className="form-group">
                <label>Subscription End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={subscriptionForm.endDate}
                  onChange={handleSubscriptionChange}
                  min={subscriptionForm.startDate || today}
                  disabled={!subscriptionForm.createOnProjectUpdate}
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={subscriptionForm.status}
                  onChange={handleSubscriptionChange}
                  disabled={!subscriptionForm.createOnProjectUpdate}
                >
                  <option value="Active">Active</option>
                  <option value="Expired">Expired</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              <div className="form-group">
                <label>Amount</label>
                <input
                  type="text"
                  name="amount"
                  inputMode="decimal"
                  pattern="^[0-9]*[.,]?[0-9]*$"
                  value={subscriptionForm.amount}
                  onChange={handleSubscriptionChange}
                  disabled={!subscriptionForm.createOnProjectUpdate}
                />
              </div>
            </div>

            {subscriptionError ? (
              <div className="project-subscription-error" role="alert" style={{ marginTop: 12 }}>
                {subscriptionError}
              </div>
            ) : null}

            {subscriptionResult ? (
              <div className="project-subscription-success" style={{ marginTop: 12 }}>
                Subscription created successfully. Amount: {subscriptionResult.Amount ?? "—"} | End:{" "}
                {subscriptionResult.End_Date ?? "—"}
              </div>
            ) : null}
          </div>
        </div>

        <div
          className="form-actions edittenant-actions"
          style={{
            display: "inline-flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            alignItems: "center",
            gap: 10,
            borderTop: "none",
            paddingTop: 0
          }}
        >
          <button type="submit" className="btn-subproject-primary" disabled={submitting}>
            {submitting ? "Updating..." : "Update Project"}
          </button>
          <button
            type="button"
            className="btn-cancel"
            onClick={navigateBackToProjects}
          >
            Cancel
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

export default EditProject;
